import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { ICellProps, IColumn } from '../interfaces';
import ExpandCell from '../../ExpandableTable/ExpandCell';
import tableStyles from '../dataTable.module.scss';

type ExtendableDataItem<T> = Record<string, any> & {
  children?: Array<Partial<T>>,
};

interface IExpandableCellConfig {
  className?: string,
  position?: number,
  stickyLeft?: boolean,
  labels?: {
    expand?: string,
    collapse?: string,
  },
}

interface IProps<T> {
  data: T[],
  columns: IColumn[],
  childrenVisibleColumns: string[],
  rowClassName?: string,
  expandableRowClassName?: string,
  expandedRowClassName?: string,
  childrenRowClassName?: string,
  expandedByDefault?: boolean,
  expandableCellConfig?: IExpandableCellConfig,
}

export interface IExpandedTableColumn extends IColumn {
  cellCallback?: (
    data: ICellProps & {
      toggleRowExpanding: () => void,
    },
  ) => React.ReactNode,
}

export type ExpandedFullItem<T> = T & {
  expandableKey: string,
  isExpandable: boolean,
  isExpanded: boolean,
  isParent: boolean,
};

type ModifiedDataItem<T> = ExpandedFullItem<T> | Partial<T>;

export const useExpandableTable = <T extends ExtendableDataItem<T>>({
  data: originData,
  columns: originColumns,
  childrenVisibleColumns,
  rowClassName = tableStyles.expandedTableRow,
  expandableRowClassName = tableStyles.expandableRow,
  expandedRowClassName = tableStyles.expandedRow,
  childrenRowClassName = tableStyles.expandableChildrenRow,
  expandedByDefault = true,
  expandableCellConfig,
}: IProps<T>) => {
  const isRowExpandable = (row: T) => row.children?.length > 0;
  const getRowExpandableKey = (_: T, index: number) => index.toString();

  const allExpandableRowsIndexes = useMemo(() => {
    return originData.reduce((acc, row, index) => {
      if (isRowExpandable(row)) {
        return [...acc, getRowExpandableKey(row, index)];
      }

      return acc;
    }, []);
  }, [originData]);

  const [expandableRows, setExpandableRows] = useState<string[]>(expandedByDefault ? allExpandableRowsIndexes : []);

  useEffect(
    () => setExpandableRows(expandedByDefault ? allExpandableRowsIndexes : []),
    [allExpandableRowsIndexes, expandedByDefault],
  );

  const isThereExpandableRows = allExpandableRowsIndexes.length > 0;
  const isAllRowsExpanded = allExpandableRowsIndexes.length === expandableRows.length;

  const toggleRowExpanding = (id: string) => {
    setExpandableRows((rows) => {
      if (rows.includes(id)) {
        return rows.filter((row) => row !== id);
      }

      return [...rows, id];
    });
  };

  const toggleAllExpandableRows = () => {
    if (isAllRowsExpanded) {
      setExpandableRows([]);
    } else {
      setExpandableRows(allExpandableRowsIndexes);
    }
  };

  const onRowClick = (event: Event, { isExpandable, expandableKey }: ModifiedDataItem<T>) => {
    if (!isExpandable) {
      return;
    }

    /**
     * Prevent to trigger event on active elements
     */
    const target = event.target as HTMLElement;
    const tagName = target.tagName.toLowerCase();
    const listOfActiveTags = ['a', 'button'];

    const isFiredOnInteractiveElement = listOfActiveTags.some((tag) => {
      return listOfActiveTags.includes(tagName) || !!target.closest(tag);
    });

    if (isFiredOnInteractiveElement) {
      return;
    }

    toggleRowExpanding(expandableKey);
  };

  const data = useMemo(
    () => originData.reduce<ModifiedDataItem<T>[]>((acc, row, index) => {
      const expandableKey = getRowExpandableKey(row, index);
      const isExpandable = isRowExpandable(row);
      const isExpanded = expandableRows.includes(expandableKey);

      return [
        ...acc,
        {
          ...row,
          parentRowIndex: index,
          expandableKey,
          isExpandable,
          isExpanded,
          isParent: true,
          className: cn(row.className, rowClassName, {
            [expandableRowClassName]: isExpandable,
            [expandedRowClassName]: isExpanded,
          }),
        },
        ...(isExpanded && isExpandable
          ? row.children.map((child) => ({
            ...child,
            className: cn(child.className, rowClassName, childrenRowClassName),
          }))
          : []),
      ];
    }, []),
    [originData, expandableRows, rowClassName, expandableRowClassName, expandedRowClassName, childrenRowClassName],
  );

  const columns = useMemo<IExpandedTableColumn[]>(() => {
    let columns = [
      /**
       * Remove useless cell callback for children rows
       */
      ...originColumns.map((column) => {
        if (childrenVisibleColumns.includes(column.name)) {
          return column;
        }

        return {
          ...column,
          cellCallback: (cellProps) => {
            if (!cellProps.row.isParent) {
              return '';
            }

            if (column.cellCallback) {
              return column.cellCallback({
                ...cellProps,
                toggleRowExpanding: () => toggleRowExpanding(cellProps.row.expandableKey),
              });
            }

            return cellProps.cellData ?? '';
          },
        };
      }),
    ];

    /**
     * Add expand status column if there is some expandable rows
     */
    if (isThereExpandableRows) {
      const {
        className,
        labels,
        position,
        stickyLeft,
      } = expandableCellConfig;

      columns.splice(position ?? -1, 0, {
        name: 'expandableState',
        className,
        stickyLeft,
        cellCallback: ({ row }) => {
          if (row.isExpandable) {
            return (
              <ExpandCell
                isExpanded={ row.isExpanded }
                // @ts-ignore
                expandLabel={ labels?.expand }
                collapseLabel={ labels?.collapse }
              />
            );
          }

          return '';
        },
      });
    }

    return columns;
  }, [isThereExpandableRows, expandableRows, originColumns, childrenVisibleColumns]);

  return {
    isThereExpandableRows,
    isAllRowsExpanded,
    toggleAllExpandableRows,
    tableProps: {
      columns,
      data,
      rowCallbacks: {
        onClick: onRowClick,
      },
    },
  };
};
